import { Box, Typography, Divider } from '@mui/material';
import BalanceCardButton from './BalanceDashbaordCardButton';
import DigitalBalanceDisabledBanner from './DigitalBalanceDisabledBanner';
import { BalanceDepositType } from 'constants/balance.constants';

export interface BalanceDashboardCardProps {
	onActionClick: () => void;
	disabled?: boolean;
	loading?: boolean;
	title: string;
	description: string;
	buttonText: string;
	imagePath: string;
	balanceDepositType: BalanceDepositType;
	digitalBalanceDisabledText?: string | null;
	lang: string;
}

export default function BalanceDashboardCard({
	onActionClick,
	disabled,
	loading,
	title,
	description,
	buttonText,
	imagePath,
	balanceDepositType,
	digitalBalanceDisabledText,
	lang,
}: BalanceDashboardCardProps) {
	const isDigital = balanceDepositType === BalanceDepositType.Digital;
	const isNew = lang === 'he' ? title.toLowerCase().startsWith('חדש!') : title.toLowerCase().startsWith('new!');
	let cleanTitle = title;
	let newText;

	if (isNew) {
		cleanTitle = lang === 'he' ? title.replace('חדש!', '').trim() : title.replace(/new!/i, '').trim();
		newText = lang === 'he' ? 'חדש! ' : 'New! ';
	}

	const imageStyle = {
		position: 'relative',
		width: {
			xxs: isDigital ? '100px' : '100px',
			xs: isDigital ? '140.99px' : '129px',
			md: isDigital ? '187.91px' : '148px',
		},
		height: {
			xs: isDigital ? '91.64px' : '112px',
			md: isDigital ? '122.14px' : '129px',
		},
		transform: isDigital ? 'rotate(3.66deg)' : 'rotate(0deg)',
		bottom: {
			xs: isDigital ? '20px' : '30px',
			sm: isDigital ? '20px' : '30px',
			md: isDigital ? '43px' : '73px',
		},
		right: {
			xs: isDigital ? '-10px' : '0px',
			md: '0',
		},
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: '#EDF5F2',
				border: '1px solid #D7DAD9',
				borderRadius: '0.625rem',
				width: { xs: '100%', md: 'calc(50% - 8px)' },
				height: {
					xs: 'auto', // Auto height to accommodate long descriptions
					md: 'auto',
				},
				minWidth: { xs: '280px', md: '500px' },
				minHeight: { xs: '320px', md: '286px' },
				overflow: 'hidden',
				boxSizing: 'border-box',
			}}
		>
			<Box
				sx={{
					padding: {
						xs: '0.5rem',
						md: '48px 13px 2rem 13px',
					},
					display: 'flex',
					flexDirection: 'column',
					flex: '1',
					boxSizing: 'border-box',
				}}
			>
				{/* Title Section */}
				<Box sx={{ width: 'fit-content', marginBottom: '1rem' }}>
					<Typography
						variant="h5"
						sx={{
							fontFamily: 'RubikRegular',
							color: '#146054',
							fontWeight: 600,
							fontSize: {
								xs: '1rem',
								md: '1.125rem',
							},
							lineHeight: 1.3,
						}}
					>
						{isDigital && isNew && (
							<Box component="span" sx={{ color: '#F36F23', fontSize: '1.125rem', fontWeight: 600 }}>
								{newText}
							</Box>
						)}
						{cleanTitle}
					</Typography>
					<Divider sx={{ borderColor: '#004D35', borderBottomWidth: 2 }} />
				</Box>

				{/* Description Section - Flexible Height Container */}
				<Box
					sx={{
						marginBottom: '1rem',
						display: 'flex',
						alignItems: 'flex-start',
						minHeight: {
							xs: '40px', // Minimum height to ensure consistency
							md: '60px',
						},
					}}
				>
					<Typography
						variant="body1"
						sx={{
							color: '#000000',
							fontSize: {
								xs: '18px',
								md: '0.875rem',
							},
							width: {
								xs: isDigital ? '100%' : '290px',
							},
							lineHeight: 1.43,
							maxWidth: '20.25rem',
							whiteSpace: 'pre-line',
						}}
					>
						{description.replaceAll('\\n', '\n')}
					</Typography>
				</Box>

				{/* Button and Image Section - Fixed Position */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						height: { xs: 'auto', md: '60px' },
						marginTop: 'auto', // Push to bottom
					}}
				>
					{/* Button Container */}
					<Box>
						<BalanceCardButton
							text={buttonText}
							disabled={disabled}
							loading={loading}
							onClick={onActionClick}
							buttonOption={isDigital ? BalanceDepositType.Digital : BalanceDepositType.Manual}
							fullWidth={false}
						/>
					</Box>

					{/* Image Container */}
					<Box component="img" sx={{ ...imageStyle }} src={imagePath} alt="Deposit illustration"></Box>
				</Box>
			</Box>
			{/* Digital Balance Disabled Banner */}
			<Box sx={{ minHeight: '38px' }}>
				{digitalBalanceDisabledText && <DigitalBalanceDisabledBanner text={digitalBalanceDisabledText} />}
			</Box>
		</Box>
	);
}
